import axios from '@axios'

const CREATIVE_SERVICE_URL = process.env.VUE_APP_CREATIVE_SERVICE_URL
const UPLOAD_SERVICE_URL = process.env.VUE_APP_UPLOAD_URL

export default {
  // Upload image
  uploadImage(file) {
    const formData = new FormData()
    formData.append('file', file)

    return axios.post(`${UPLOAD_SERVICE_URL}/api/upload/image`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: progressEvent => {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        return progress
      },
    })
  },

  // Upload video
  uploadVideo(file) {
    const formData = new FormData()
    formData.append('file', file)

    return axios.post(`${UPLOAD_SERVICE_URL}/api/upload/video`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: progressEvent => {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        return progress
      },
    })
  },

  // Generate image prompt
  generateImagePrompt(imageUrl) {
    const formData = new FormData()
    formData.append('imageUrl', imageUrl)

    return axios.post(`${CREATIVE_SERVICE_URL}/api/creative/video-prompt/generate`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },

  // Generate video from image and prompt
  generateVideo(imageUrl, prompt) {
    const payload = {
      imageUrl,
      textPrompt: prompt,
    }

    return axios.post(`${CREATIVE_SERVICE_URL}/api/creative/image-to-video/task`, payload, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
  },

  // Poll video generation status
  pollVideoStatus(taskIds) {
    // Convert array to URL-encoded format: ["taskId1"] -> [%22taskId1%22]
    const encodedTaskIds = encodeURIComponent(JSON.stringify(taskIds))

    return axios.get(`${CREATIVE_SERVICE_URL}/api/creative/video/task/list?taskIds=${encodedTaskIds}`)
  },

  // Fetch video list
  fetchVideoList({
    page = 1,
    limit = 15,
    types = ['IMAGE_TO_BYTEPLUS_VIDEO', 'MANUAL_UPLOAD_SOURCE_VIDEO'],
    type,
  } = {}) {
    // Normalize provided type(s) into an array
    const normalizedTypes = []
    if (Array.isArray(types)) normalizedTypes.push(...types)
    else if (types) normalizedTypes.push(types)
    if (type) normalizedTypes.push(type)

    // Build query string to guarantee types[]=A&types[]=B (avoid types[][])
    const searchParams = new URLSearchParams()
    searchParams.append('page', page)
    searchParams.append('limit', limit)
    normalizedTypes.forEach(t => searchParams.append('types[]', t))

    const url = `${CREATIVE_SERVICE_URL}/api/creative/video/list?${searchParams.toString()}`
    return axios.get(url)
  },

  // Upload video source
  uploadVideoSource(file) {
    const formData = new FormData()
    formData.append('video', file)
    formData.append('type', 'MANUAL_UPLOAD_SOURCE_VIDEO')

    return axios.post(`${CREATIVE_SERVICE_URL}/api/creative/video/source/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: progressEvent => {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        return progress
      },
    })
  },

  // Generate form video
  generateFormVideo(payload) {
    const formData = new FormData()

    // Add images from selected videos (if they have file objects)
    payload.selectedVideos.forEach(video => {
      if (video.file) {
        formData.append('images', video.file)
      }
    })

    // Add videos from selected videos
    payload.selectedVideos.forEach(video => {
      if (video.file && video.type === 'video') {
        formData.append('videos', video.file)
      }
    })

    // Add videoUrls if available
    if (payload.videoUrls && payload.videoUrls.length > 0) {
      // Also add as a single parameter for the API
      formData.append('videoUrls', JSON.stringify(payload.videoUrls))
    }

    // Add product type
    formData.append('productType', payload.productType)

    // Add product video info as JSON string
    formData.append('productVideoInfo', JSON.stringify(payload.productVideoInfo))
    if (payload.advertiserId) {
      formData.append('advertiserId', payload.advertiserId)
    }

    if (payload.avatarInfo?.avatarId) {
      formData.append('avatarInfo', JSON.stringify(payload.avatarInfo))
    }

    return axios.post(`${CREATIVE_SERVICE_URL}/api/creative/video/task`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },

  // AI Analyze image
  analyzeImageWithAI(imageUrl) {
    const formData = {
      imageUrl,
    }

    return axios.post(`${CREATIVE_SERVICE_URL}/api/creative/generate-info-based-on-image`, formData, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    })
  },

  // Fetch prompt suggestions
  fetchPromptSuggestions(params = { limit: 200 }) {
    return axios.get(`${CREATIVE_SERVICE_URL}/api/creative/prompt-suggestions`, {
      params,
    })
  },

  // Generate image from image (Image to Image)
  generateImageFromImage(payload) {
    return axios.post(`${CREATIVE_SERVICE_URL}/api/creative/image-generation/genai`, payload, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
  },

  // Poll image generation status
  pollImageGenerationStatus(imageGenerationIds) {
    const ids = Array.isArray(imageGenerationIds) ? imageGenerationIds : [imageGenerationIds]
    const queryString = `[${ids.map(id => `"${id}"`).join(',')}]`

    return axios.get(`${CREATIVE_SERVICE_URL}/api/creative/image-generations`, {
      params: {
        imageGenerationIds: queryString,
      },
    })
  },
}
